import {useEffect, useCallback, useState, useRef} from 'react';
import {observer} from 'mobx-react-lite';
import styled from 'styled-components';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {useStoreContext} from '../../../../store/store-context';
import {CommentsBodyStyle, UserHeader} from '../comments-style';
import {Body2} from '../../../atoms/typography';
import {DropdownVertical} from '../../../molecules/dropdown-vertical';
import {DropdownItem} from '../../../../pages/dashboard/admin/admin-components/admin-people/admin-group-type/admin-group-type-style';
import {Deletedata} from '../../../../utils/firebase-request';
import {Avatar} from '../../../atoms/avatar';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {cloudinary} from '@assets/images/cloudinary';
import {MoreVerticalIcon2} from '@ui/atoms/icons/more-vertical';
import {FailedComment} from './comment-failed';
import {authStore} from '@store/stores/auth-store';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {COMMENT_MENTION_CLASSNAME} from '@ui/atoms/typography/comment-mention';
import {rawContentToMarkdown} from '@ui/molecules/editor/rich-text.utils';
import {formatCommentTimestamp} from './comment-time.utils';
import {getColorFromTheme} from '@ui/ui-utils';

const {emptyGoals: emptyGoal} = cloudinary;

// Styled Components
const CommentContainer = styled.div`
  padding-top: 5px;
  padding-bottom: 12px;
`;

const DateDividerWrapper = styled.div<{hasDate: boolean}>`
  display: flex;
  border: 0.5px solid ${getColorFromTheme('borderLight')};
  margin-top: 7px;
  width: ${(props) => (props.hasDate ? '95%' : '100%')};
  margin: ${(props) =>
    props.hasDate ? '5px 20px 5px 20px' : '5px 0px 5px 0px'};
  height: 0px;
  align-items: center;
  justify-content: center;
`;

const DateBadge = styled.div`
  width: 111px;
  height: 26px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 6px;
  background: ${getColorFromTheme('backgroundDark')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DateText = styled(Body2)`
  font-size: 12px;
  font-weight: 500;
`;

const CommentContentWrapper = styled.div`
  padding: 10px 12px 4px 12px;
`;

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled(Body2)`
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  margin-left: 9px;
`;

const CommentBody = styled.div`
  display: block;
`;

const CommentTextWrapper = styled(CommentsBodyStyle)`
  width: 100%;
  margin-top: 10px;
  margin-left: 8px;
`;

const CommentTextContent = styled.div`
  min-width: 0;
  flex: 1;
  color: ${({theme}) => theme.colors.blackTwo};
  font-size: 14px;
  line-height: 22px;
  overflow-wrap: anywhere;
`;

const EditedLabel = styled.span`
  margin-left: 6px;
  color: ${({theme}) => theme.colors.textMuted};
  font-size: 12px;
`;

const TimeStamp = styled(Body2)`
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

const CommentHeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const EmptyStateWrapper = styled.div`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 0px 0px 10px 10px;
`;

const SimpleEmptyState = styled.div`
  padding: 36px 16px 52px;
  color: ${getColorFromTheme('textMuted')};
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;

const ScrollAnchor = styled.div``;

// Constants
const EMPTY_QUESTIONS_CONFIG = {
  image: {
    alt: 'alt text',
    shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
    src: emptyGoal,
    style: {height: '30%', width: '30%'},
  },
  subtitle: '',
  title: `No comments added yet.`,
};

// Configure dayjs plugins
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(advancedFormat);

interface CommentsBodyProps {
  comments: any;
  goalId: string;
  editComments: any;
  path: string;
  handleEditComments: (item: any) => void;
  mentions?: string[];
  richTextEntities?: Record<string, unknown>;
  simpleEmptyState?: boolean;
}

export const CommentsBody = observer(
  ({
    comments,
    goalId,
    editComments,
    path,
    handleEditComments,
    mentions,
    richTextEntities = {},
    simpleEmptyState,
  }: CommentsBodyProps) => {
    const {
      usersStore: {users, currentUser},
    } = useStoreContext();

    const myRef = useRef<HTMLDivElement>(null);
    const dates = useRef(new Set<string>()).current;

    const [redirectComment] = useState('');
    const [getMentionComment, setMentionedComment] = useState<any>([]);

    const renderLegacyCommentText = useCallback(
      (comment: any) => {
        const text = String(comment?.comment || '');
        const parts: (string | JSX.Element)[] = [];
        const mentionPattern = /_\(([^)]+)\)_/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = mentionPattern.exec(text))) {
          if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
          }

          const userId = match[1];
          const user = users.find((item: any) => item.id === userId);
          const name = user
            ? user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email || 'Team member'
            : 'Team member';

          parts.push(
            <span
              className={COMMENT_MENTION_CLASSNAME}
              data-mention-user-id={userId}
              key={`${userId}-${match.index}`}>
              @{name}
            </span>,
          );
          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
          parts.push(text.slice(lastIndex));
        }

        return parts.length ? parts : text;
      },
      [users],
    );

    const executeScroll = useCallback(() => {
      myRef?.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, []);

    const items = useCallback(() => {
      if (comments) {
        return Object.entries(comments);
      }
      return [];
    }, [comments]);

    const renderDate = useCallback(
      (chat: string, dateNum: string) => {
        dates.add(dateNum);
        const chatDate = dayjs(chat);

        let dateLabel = chatDate.format('Do MMM YYYY');
        if (chatDate.isToday()) {
          dateLabel = 'Today';
        } else if (chatDate.isYesterday()) {
          dateLabel = 'Yesterday';
        }

        return (
          <DateBadge>
            <DateText kind="textMedium">{dateLabel}</DateText>
          </DateBadge>
        );
      },
      [dates],
    );

    const getUserRef = useCallback(() => {
      const res = items()
        .reverse()
        .filter((item: any) => item[1]?.mentions?.includes(currentUser.id));
      setMentionedComment(res[0]);
    }, [items, currentUser.id]);

    useEffect(() => {
      if (myRef.current) {
        executeScroll();
      }
    }, [executeScroll, redirectComment]);

    useEffect(() => {
      getUserRef();
    }, [getUserRef]);

    const commentItems = items();
    const hasComments = commentItems?.length > 0;

    const getUserName = (user: any) => {
      if (user?.firstName && user?.lastName) {
        return `${user.firstName} ${user.lastName}`;
      }
      return user?.email || '';
    };

    const handleDeleteComment = useCallback(
      (commentId: string, handleClose: () => void) => {
        Deletedata(path, commentId);
        if (editComments) {
          handleEditComments('');
        }
        handleClose();
      },
      [path, editComments, handleEditComments],
    );

    const handleEditClick = useCallback(
      (item: any, handleClose: () => void) => {
        handleEditComments(item);
        handleClose();
      },
      [handleEditComments],
    );

    return (
      <div>
        {hasComments ? (
          commentItems.reverse().map((item: any) => {
            const [commentId, commentData] = item;
            const formattedDate = dayjs(commentData.createdAt).format(
              'Do MMM YYYY',
            );
            const hasDateInSet = dates.has(formattedDate);
            const isUserMentioned =
              mentions?.includes(currentUser.id) &&
              getMentionComment?.length > 0 &&
              getMentionComment[0] === commentId;
            const isRedirectComment = redirectComment === commentId;
            const richComment = rawContentToMarkdown(
              richTextEntities?.[commentId] || commentData.entities,
            );

            return (
              <CommentContainer key={commentId}>
                <DateDividerWrapper hasDate={hasDateInSet}>
                  {!hasDateInSet &&
                    renderDate(commentData.createdAt, formattedDate)}
                </DateDividerWrapper>

                {isUserMentioned && <ScrollAnchor ref={myRef} />}
                {isRedirectComment && <ScrollAnchor ref={myRef} />}

                <CommentContentWrapper>
                  <UserHeader>
                    {users
                      ?.filter(
                        (user: any) => user?.id === commentData.commentBy,
                      )
                      .map((user: any) => (
                        <UserInfoContainer key={user.id}>
                          <Avatar
                            size="md"
                            name={getUserName(user)}
                            src={user?.avatar?.url}
                            userId=""
                            tooltip={false}
                          />
                          <UserName>{getUserName(user)}</UserName>
                        </UserInfoContainer>
                      ))}

                    <CommentHeaderActions>
                      <TimeStamp kind="textMedium">
                        {formatCommentTimestamp(commentData?.createdAt)}
                      </TimeStamp>
                      {authStore.auth.user.id === commentData.commentBy && (
                        <DropdownVertical
                          collapseOnClick={true}
                          dropdownWrapperStyle={{right: 0}}
                          customIcon={<MoreVerticalIcon2 />}
                          menu={(handleClose: () => void) => (
                            <div>
                              <DropdownItem
                                onClick={() =>
                                  handleEditClick(item, handleClose)
                                }>
                                Edit Comment
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handleDeleteComment(commentId, handleClose)
                                }>
                                Delete Comment
                              </DropdownItem>
                            </div>
                          )}
                        />
                      )}
                    </CommentHeaderActions>
                  </UserHeader>

                  <CommentBody>
                    <CommentTextWrapper>
                      <CommentTextContent>
                        {richComment ? (
                          <CustomMarkdown text={richComment} compact />
                        ) : (
                          <span>{renderLegacyCommentText(commentData)}</span>
                        )}
                        {commentData.edited && (
                          <EditedLabel>(edited)</EditedLabel>
                        )}
                      </CommentTextContent>
                    </CommentTextWrapper>
                  </CommentBody>
                </CommentContentWrapper>
              </CommentContainer>
            );
          })
        ) : (
          <>
            {simpleEmptyState ? (
              <SimpleEmptyState role="status">
                No comments yet.
              </SimpleEmptyState>
            ) : (
              <EmptyStateWrapper>
                <EmptyPlaceholder {...EMPTY_QUESTIONS_CONFIG} />
              </EmptyStateWrapper>
            )}
          </>
        )}

        {localStorage.getItem('failComment') && (
          <FailedComment users={users} currentUser={goalId} />
        )}
      </div>
    );
  },
);
